import Logger from '../../Logging';
import { CreateReduxConnector } from '../ReduxConnector';

import { setAreTotalDevicesChanged, setMediaInformation, setNewLocalMediaStream, updateVideoResolutionObject, setNewMediaDevice, setVideoPermission, setAudioPermission, setIsProcessing } from '../../../Redux/Actions/mediaActions';
import {BlurHandler} from './BlurHandler';


const INFO = 'Utilities/Gnural/LocalMediaHandler';

const ReduxConnector = CreateReduxConnector();

class LocalMediaHandler {
	constructor() {
		this.init = this.init.bind(this);
		this.promptLocalMedia = this.promptLocalMedia.bind(this);
		this.getLocalMediaInformation = this.getLocalMediaInformation.bind(this);
		this.updateProperties = this.updateProperties.bind(this);
		this.StartLocalMedia = this.StartLocalMedia.bind(this);
		this.StopLocalMedia = this.StopLocalMedia.bind(this);
		this.getLocalMediaStream = this.getLocalMediaStream.bind(this);
		this.getResolutions = this.getResolutions.bind(this);
		this.awaitMutex = this.awaitMutex.bind(this);
		this.onDevicesChanged = this.onDevicesChanged.bind(this);
		this.mutex = false;
		this.startLocalMediaMutex = false;
		this.isInitialized = false;
		this.blurHandler = null;

		this.props = {
			currentAudioInput: {},
			currentAudioOutput: {},
			currentVideoInput: {},
			currentVideoResolution: {},
			videoResolutionObject: {},
			autoGainControl: true,
			voiceOptimization: true,
			lowBandwidthMode: false,
			highBandwidthMode: false,
			enableBlurBackground: false,
		};
		this.localStream = undefined;

		if (navigator?.mediaDevices)
		{
			navigator.mediaDevices.ondevicechange = this.onDevicesChanged;
		}
	}

	possibleResolutionList = [
		{width: 1920, height:1080, friendlyName: '1920x1080'},
		{width: 1280, height: 720, friendlyName: '1280x720'},
		{width: 960, height: 720, friendlyName: '960x720'},
		{width: 960, height: 540, friendlyName: '960x540'},
		{width: 640, height: 480, friendlyName: '640x480'},
		{width: 640, height: 360, friendlyName: '640x360'},
		{width: 320, height: 180, friendlyName: '320x180'}
	];

	async onDevicesChanged() {
		ReduxConnector.dispatch(setAreTotalDevicesChanged(true));
	}

	async updateProperties(newProperties) {
		if (!newProperties?.hasSetBrowserInformation) {
			return;
		}
		const oldProperties = this.props;
		this.props = {
			currentAudioInput: {},
			currentAudioOutput: {},
			currentVideoInput: {},
			autoGainControl: true,
			voiceOptimization: true,
			lowBandwidthMode: false,
			highBandwidthMode: false,
			enableBlurBackground: false,
			...newProperties,
		};

		let newAudioInputDevice = this.props.currentAudioInput.deviceId || (oldProperties ? oldProperties.currentAudioInput.deviceId : '');
		let newAudioOutputDevice = this.props.currentAudioOutput.deviceId || (oldProperties ? oldProperties.currentAudioOutput.deviceId : '');
		let newVideoInputDevice = this.props.currentVideoInput.deviceId || (oldProperties ? oldProperties.currentVideoInput.deviceId : '');
		let newVideoResolution = this.props.currentVideoResolution.friendlyName;
		
		let shouldRestartMedia = false;

		if (!oldProperties || oldProperties.voiceOptimization !== this.props.voiceOptimization) {
			localStorage.setItem('DefaultVoiceOptimization', this.props.voiceOptimization);
			shouldRestartMedia = true;
		}
		if (!oldProperties || oldProperties.autoGainControl !== this.props.autoGainControl) {
			localStorage.setItem('DefaultAutoGainControl', this.props.autoGainControl);
			shouldRestartMedia = true;
		}

		if (!oldProperties || oldProperties.lowBandwidthMode !== this.props.lowBandwidthMode) {
			localStorage.setItem('DefaultLowBandwidthMode', this.props.lowBandwidthMode);
			window.LowBandwidthMode = this.props.lowBandwidthMode;
			shouldRestartMedia = true;
		}

		if (!oldProperties || oldProperties.highBandwidthMode !== this.props.highBandwidthMode) {
			localStorage.setItem('DefaultHighBandwidthMode', this.props.highBandwidthMode);
			window.HighBandwidthMode = this.props.highBandwidthMode;
			shouldRestartMedia = true;
		}

		if (!oldProperties || oldProperties.enableBlurBackground !== this.props.enableBlurBackground) {
			localStorage.setItem('enableBlurBackground', String(this.props.enableBlurBackground));
			// Screen sharing and audio-only capture keep running; this preference applies to cameras.
			if (this.#IsCameraSource(this.props.currentVideoInput.deviceId)) shouldRestartMedia = true;
		}

		if (!oldProperties || oldProperties.currentAudioInput.deviceId !== newAudioInputDevice) {
			localStorage.setItem('DefaultAudioInputDevice', newAudioInputDevice);
			shouldRestartMedia = true;
		}
		if (!oldProperties || oldProperties.currentAudioOutput.deviceId !== newAudioOutputDevice) {
			localStorage.setItem('DefaultAudioOutputDevice', newAudioOutputDevice);
			// Do something else;
		}

		if (newVideoInputDevice && (!oldProperties || oldProperties.currentVideoResolution.friendlyName !== newVideoResolution)) {
			localStorage.setItem('DefaultVideoResolution-' + newVideoInputDevice, newVideoResolution);
			shouldRestartMedia = true;
		}

		if ((oldProperties.currentVideoResolution || {}).friendlyName !== newVideoResolution) {
			shouldRestartMedia = true;
		}

		if (!oldProperties || oldProperties.currentVideoInput.deviceId !== newVideoInputDevice) {
			localStorage.setItem('DefaultVideoInputDevice', newVideoInputDevice);
			shouldRestartMedia = true;
			let videoResolutionList = this.props.videoResolutionObject[newVideoInputDevice];
			if (!videoResolutionList) {
				videoResolutionList = await this.getResolutions(newVideoInputDevice);
				ReduxConnector.dispatch(updateVideoResolutionObject(videoResolutionList, newVideoInputDevice));
				//shouldRestartMedia = false;
			}
			const defaultResolutionFriendly = localStorage.getItem('DefaultVideoResolution-' + newVideoInputDevice);
			let defaultResolution = (videoResolutionList || []).filter((resolution) => {
				return resolution.friendlyName === defaultResolutionFriendly;
			})[0];
			if (!defaultResolution) {
				defaultResolution = (videoResolutionList || [])[0] || {};
			}
			if (defaultResolution.friendlyName !== newVideoResolution) {
				ReduxConnector.dispatch(setNewMediaDevice('videoResolutionObject', defaultResolution));
				shouldRestartMedia = false;
			}
		}
		if (shouldRestartMedia) {
			this.StartLocalMedia();
		}

	}

	awaitMutex(mutexName) {
		return new Promise((resolve) => {
			const checkMutex = () => {
				if (this[mutexName]) {
					setTimeout(checkMutex, 800);
					return;
				}
				this[mutexName]= true;
				resolve();
			};
			checkMutex();
		});
	}

	#IsCameraSource(deviceId) {
		return deviceId && deviceId !== 'Screenshare' && deviceId !== 'AudioOnly';
	}

	async #StopBlurOnStream(stream) {
		const handler = this.blurHandler;
		if (!handler) return;
		await handler.Stop();
		const blurredTrack = handler.GetBlurredVideoTrack();
		// Restore only a track that belongs to this stream, never a previous camera on a screen share.
		if (blurredTrack && stream?.getVideoTracks().includes(blurredTrack)) {
			stream.removeTrack(blurredTrack);
			const originalTrack = handler.GetOriginalVideoTrack();
			if (originalTrack) stream.addTrack(originalTrack);
		}
	}

	async #ApplyBackgroundBlur(stream, videoDeviceId) {
		const originalTrack = stream.getVideoTracks()[0];
		const shouldApplyBlur = () => this.props.enableBlurBackground &&
			this.#IsCameraSource(videoDeviceId) && this.props.currentVideoInput.deviceId === videoDeviceId;
		if (!shouldApplyBlur() || originalTrack?.readyState !== 'live') return;

		if (!this.blurHandler) this.blurHandler = new BlurHandler();
		const handler = this.blurHandler;
		try {
			if (!(await handler.Init())) throw new Error('Background blur is unavailable');
			await handler.UpdateVideoTrackSource(originalTrack);
			if (!(await handler.Start())) throw new Error('Unable to start background blur');
			const blurredTrack = handler.GetBlurredVideoTrack();
			if (blurredTrack?.readyState !== 'live') throw new Error('Background blur returned no live video track');
			stream.addTrack(blurredTrack);
			stream.removeTrack(originalTrack);
		} catch (error) {
			Logger.warn(INFO, 'Unable to apply background blur; using the original camera track', error);
			const blurredTrack = handler.GetBlurredVideoTrack();
			if (blurredTrack && stream.getVideoTracks().includes(blurredTrack)) {
				stream.removeTrack(blurredTrack);
				stream.addTrack(originalTrack);
			}
			try {
				await handler.Dispose();
			} catch (disposeError) {
				Logger.warn(INFO, 'Failed to dispose background blur', disposeError);
			} finally {
				if (this.blurHandler === handler) this.blurHandler = null;
			}
		}
	}

	async StopLocalMedia() {
		// Cleanup must still run after the preference has already been switched off.
		await this.#StopBlurOnStream(this.localStream);
		this.localStream?.getTracks().forEach(track => track.stop());
	}

	async StartLocalMedia() {
		await this.awaitMutex('startLocalMediaMutex');
		let stream = null;
		let published = false;
		try {
			const properties = this.props;
			const videoDeviceId = properties.currentVideoInput.deviceId;
			ReduxConnector.dispatch(setIsProcessing(true));
			if (!properties.currentAudioInput.deviceId || !videoDeviceId) {
				Logger.warn(INFO, 'Invalid Audio Input or Video Input Device');
			}

			const rtcVideoConstraints = this.#IsCameraSource(videoDeviceId)
				? {deviceId: {exact: videoDeviceId}}
				: false;
			const rtcAudioProcessingConstraints = {
				echoCancellationType: 'browser',
				channelCount: (properties.lowBandwidthMode ? 1 : 2),
				echoCancellation: properties.voiceOptimization,
				noiseSuppression: properties.voiceOptimization,
				autoGainControl: properties.autoGainControl,
				googEchoCancellation: properties.voiceOptimization,
				googAutoGainControl: properties.autoGainControl,
				googExperimentalAutoGainControl: properties.autoGainControl,
				googNoiseSuppression: properties.voiceOptimization,
				googExperimentalNoiseSuppression: properties.voiceOptimization,
				googHighpassFilter: properties.voiceOptimization,
				googTypingNoiseDetection: properties.voiceOptimization,
				googBeamforming: properties.voiceOptimization,
				googArrayGeometry: properties.voiceOptimization,
				googAudioMirroring: properties.voiceOptimization,
				googNoiseReduction: properties.voiceOptimization,
				mozNoiseSuppression: properties.voiceOptimization,
				mozAutoGainControl: properties.autoGainControl
			};
			const rtcAudioConstraints = properties.currentAudioInput.deviceId
				? {deviceId: {exact: properties.currentAudioInput.deviceId}, ...rtcAudioProcessingConstraints}
				: false;
			if (rtcVideoConstraints && properties.currentVideoResolution?.width && properties.currentVideoResolution?.height) {
				rtcVideoConstraints.width = {exact: properties.currentVideoResolution.width};
				rtcVideoConstraints.height = {exact: properties.currentVideoResolution.height};
			} else if (rtcVideoConstraints) {
				rtcVideoConstraints.width = {ideal: 1280};
				rtcVideoConstraints.height = {ideal: 720};
			}

			await this.StopLocalMedia();
			stream = await navigator.mediaDevices.getUserMedia({video: rtcVideoConstraints, audio: rtcAudioConstraints});

			if (videoDeviceId === 'Screenshare' && navigator.mediaDevices.getDisplayMedia) {
				const screenConstraints = properties.currentVideoResolution?.width && properties.currentVideoResolution?.height
					? {width: properties.currentVideoResolution.width, height: properties.currentVideoResolution.height}
					: true;
				const screenshareStream = await navigator.mediaDevices.getDisplayMedia({video: screenConstraints});
				screenshareStream?.getVideoTracks().forEach(track => stream.addTrack(track));
			} else if (this.#IsCameraSource(videoDeviceId)) {
				await this.#ApplyBackgroundBlur(stream, videoDeviceId);
			}
			this.localStream = stream;
			ReduxConnector.dispatch(setNewLocalMediaStream(stream, properties.lowBandwidthMode, properties.highBandwidthMode));
			published = true;
		} catch (error) {
			Logger.error(INFO, 'Failed to get Local Media: ', error);
		} finally {
			try {
				if (stream && !published) {
					await this.#StopBlurOnStream(stream);
					stream.getTracks().forEach(track => track.stop());
				}
			} finally {
				this.startLocalMediaMutex = false;
				ReduxConnector.dispatch(setIsProcessing(false));
			}
		}
	}

	getLocalMediaStream() {
		return this.localStream;
	}

	async promptLocalMedia() {
		await this.awaitMutex('startLocalMediaMutex');
		let mediaConstraints = {
			video: true,
			audio: true
		};
		let stream;
		try {
			stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
			stream.getVideoTracks().forEach(track => {
				if (track) {
					track.stop();
				}
			});
			stream.getAudioTracks().forEach(track => {
				if (track) {
					track.stop();
				}
			});
			await this.getLocalMediaInformation();
			ReduxConnector.dispatch(setVideoPermission(true));
			ReduxConnector.dispatch(setAudioPermission(true));
		} catch (e) {
			Logger.error(INFO, 'Failed to Prompt Local Media: ', e);
			Logger.warn(INFO, 'Attempting to Prompt Just Video Media');
			try {
				mediaConstraints = {
					video: true,
					audio: false
				};
				stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
				stream.getVideoTracks().forEach(track => {
					if (track) {
						track.stop();
					}
				});
				await this.getLocalMediaInformation();
				ReduxConnector.dispatch(setVideoPermission(true));
			} catch (e) {
				Logger.error(INFO, 'Failed to Prompt Just Video Media: ', e);
				if (e.name === 'NotAllowedError') {
					ReduxConnector.dispatch(setVideoPermission(false));
				}
			}
			Logger.warn(INFO, 'Attempting to Prompt Just Audio Media');
			try {
				mediaConstraints = {
					video: false,
					audio: true
				};
				stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
				stream.getAudioTracks().forEach(track => {
					if (track) {
						track.stop();
					}
				});
				ReduxConnector.dispatch(setAudioPermission(true));
			} catch (e) {
				if (e.name === 'NotAllowedError') {
					ReduxConnector.dispatch(setAudioPermission(false));
				}
				Logger.error(INFO, 'Failed to Prompt Just Audio Media: ', e);
			}
		}
		this.startLocalMediaMutex = false;
	}

	async tryMediaAsync(constraints, resolution) {
		try {
			const videoStream = await navigator.mediaDevices.getUserMedia(constraints);
			if (videoStream) {
				videoStream.getVideoTracks().forEach((track) => {
					track.stop();
				});
			}
			return resolution;
		} catch (e) {
			return null;
		}
	}

	async getResolutions(deviceId) {
		if (!deviceId || !this.props.BrowserName) {
			return [];
		}
		if (deviceId === 'Screenshare') {
			return this.possibleResolutionList;
		}

		ReduxConnector.dispatch(setIsProcessing(true));

		let possibleResolutionList = [
			{width: 1920, height:1080, friendlyName: '1920x1080'},
			{width: 1280, height: 720, friendlyName: '1280x720'},
			{width: 960, height: 720, friendlyName: '960x720'},
			{width: 960, height: 540, friendlyName: '960x540'},
			{width: 640, height: 480, friendlyName: '640x480'},
			{width: 640, height: 360, friendlyName: '640x360'},
			{width: 320, height: 180, friendlyName: '320x180'}
		];
		if (this.props.isSafari) {
			possibleResolutionList = [
				{width: 1280, height: 720, friendlyName: '1280x720'},
				{width: 640, height: 480, friendlyName: '640x480'},
				{width: 320, height: 240, friendlyName: '320x240'}
			];
		}

		const videoResolutionRequestList = [];
		const finalVideoResolutionList = [];	

		let constraint;

		try {
			let isValid = await this.tryMediaAsync({video: {deviceId: {exact: deviceId}}, audio: false}, 'PROMPT');
			if (isValid == null) {
				Logger.error(INFO, 'Media Device: ' + deviceId + ' Can Not Be Started');
				ReduxConnector.dispatch(setIsProcessing(false));
				return [];
			}
		} catch {
			ReduxConnector.dispatch(setIsProcessing(false));
			return [];
		}
		
		possibleResolutionList.forEach(resolution => {
			constraint = {
				video: {
					deviceId: {exact: deviceId},
					width: {exact: resolution.width},
					height: {exact: resolution.height}
				},
				audio: false
			};
			videoResolutionRequestList.push(this.tryMediaAsync(constraint, resolution));
		});

		let response;
		for (let index = 0; index < videoResolutionRequestList.length; index++) {
			try {
				response = await videoResolutionRequestList[index];
			} catch {
				response = null;
			}
			if (response) {
				finalVideoResolutionList.push(response);
			}
		}
		
		ReduxConnector.dispatch(setIsProcessing(false));
		return finalVideoResolutionList;	
	}

	async getLocalMediaInformation() {
		try {
			const mediaDevices = await navigator.mediaDevices.enumerateDevices();
			const audioInputList = [];
			const audioOutputList = [];
			const videoInputList = [];
			
			let audioOutputCounter = 1;
			let audioInputCounter = 1;
			let videoInputCounter = 1;
			mediaDevices.forEach(device => {
				if (!device || !device.kind) {
					return;
				}
				switch(device.kind) {
				case 'audioinput': {
					if (!device.label) {
						return audioInputList.push({
							deviceId: device.deviceId,
							groupId: device.groupId,
							kind: device.kind,
							label: 'Audio Input Device ' + audioInputCounter++
						});
					}
					return audioInputList.push(device);
				}
				case 'audiooutput': {
					if (!device.label) {
						return audioOutputList.push({
							deviceId: device.deviceId,
							groupId: device.groupId,
							kind: device.kind,
							label: 'Audio Output Device ' + audioOutputCounter++
						});
					}
					return audioOutputList.push(device);
				}
				case 'videoinput': {
					if (!device.label) {
						return videoInputList.push({
							deviceId: device.deviceId,
							groupId: device.groupId,
							kind: device.kind,
							label: 'Video Input Device ' + videoInputCounter++
						});
					}
					return videoInputList.push(device);
				}
				default:
					return;
				}
			});
			if (this.props.isChrome && Number.parseInt(this.props.BrowserVer.split('.')[0]) >= 72) {
				videoInputList.push({deviceId: 'Screenshare', groupId: 'Screenshare', kind: 'videoinput', label: 'Screenshare'});
			}
			videoInputList.push({deviceId: 'AudioOnly', groupId: 'AudioOnly', kind: 'videoinput', label: 'Audio Only'});

			const defaultAudioInputDeviceID = localStorage.getItem('DefaultAudioInputDevice');
			const defaultAudioOutputDeviceID = localStorage.getItem('DefaultAudioOutputDevice');
			const defaultVideoInputDeviceID = localStorage.getItem('DefaultVideoInputDevice');

			if (localStorage.getItem('DefaultAutoGainControl') === null)
			{
				localStorage.setItem('DefaultAutoGainControl', 'true');
			}
			if (localStorage.getItem('DefaultVoiceOptimization') === null)
			{
				localStorage.setItem('DefaultVoiceOptimization', 'true');
			}

			if (localStorage.getItem('DefaultLowBandwidthMode') === null)
			{
				localStorage.setItem('DefaultLowBandwidthMode', 'false');
			}

			if (localStorage.getItem('DefaultHighBandwidthMode') === null)
			{
				localStorage.setItem('DefaultHighBandwidthMode', 'false');
			}

			const defaultAutoGainControl = (localStorage.getItem('DefaultAutoGainControl') === 'true');
			const defaultVoiceOptimization = (localStorage.getItem('DefaultVoiceOptimization') === 'true');
			const defaultLowBandwidthMode = (localStorage.getItem('DefaultLowBandwidthMode') === 'true');
			const defaultHighBandwidthMode = (localStorage.getItem('DefaultHighBandwidthMode') === 'true') && !defaultLowBandwidthMode;
			window.LowBandwidthMode = defaultLowBandwidthMode;
			window.HighBandwidthMode = defaultHighBandwidthMode;

			const defaultAudioInputDevice = audioInputList.filter(device => device.deviceId === defaultAudioInputDeviceID)[0] || audioInputList[0] || {};
			const defaultAudioOutputDevice = audioOutputList.filter(device => device.deviceId === defaultAudioOutputDeviceID)[0] || audioOutputList[0] || {};
			const defaultVideoInputDevice = videoInputList.filter(device => device.deviceId === defaultVideoInputDeviceID)[0] || videoInputList[0] || {};
		
			const videoResolutionObject = {};
			let defaultVideoResolution = {};
			if (defaultVideoInputDevice) {
				const videoResolutions = await this.getResolutions(defaultVideoInputDevice.deviceId);
				defaultVideoResolution = localStorage.getItem('DefaultVideoResolution-' + defaultVideoInputDevice.deviceId);
				if (!defaultVideoResolution && videoResolutions.length >= 1) {
					defaultVideoResolution = videoResolutions[0];
				}
				videoResolutionObject[defaultVideoInputDevice.deviceId] = videoResolutions;
			}
			if (!defaultVideoResolution) {
				defaultVideoResolution = {};
			}

			// Read the latest preference after asynchronous device/resolution discovery.
			if (localStorage.getItem('enableBlurBackground') === null) {
				localStorage.setItem('enableBlurBackground', 'false');
			}
			const enableBlurBackground = localStorage.getItem('enableBlurBackground') === 'true';

			ReduxConnector.dispatch(setMediaInformation({
				audioInputList,
				audioOutputList,
				videoInputList,
				videoResolutionObject,
				currentAudioInput: defaultAudioInputDevice,
				currentAudioOutput: defaultAudioOutputDevice,
				currentVideoInput: defaultVideoInputDevice,
				currentVideoResolution: defaultVideoResolution,
				autoGainControl: defaultAutoGainControl,
				voiceOptimization: defaultVoiceOptimization,
				lowBandwidthMode: defaultLowBandwidthMode,
				highBandwidthMode: defaultHighBandwidthMode,
				enableBlurBackground,
			}));

		} catch (e) {
			Logger.error(INFO, 'Failed to get Local Media Information: ', e);
		}
	}

	async refreshLocalMedia() {
		if (!this.isInitialized || this.mutex) return;
		this.mutex = true;
		try {
			await this.awaitMutex('startLocalMediaMutex');
			try {
				await this.StopLocalMedia();
				await this.getLocalMediaInformation();
			} finally {
				this.startLocalMediaMutex = false;
			}
			await this.StartLocalMedia();
			ReduxConnector.dispatch(setAreTotalDevicesChanged(false));
		} finally {
			this.mutex = false;
		}
	}

	async init() {
		if (this.mutex) {
			return;
		}
		this.mutex = true;
		await this.promptLocalMedia();
		//await this.getLocalMediaInformation();
		this.mutex = false;
		this.isInitialized = true;
	}
}

function neededStoreVariables(store) {
	return {...store.media, ...store.sessionInformation};
}

const Handler = new LocalMediaHandler();

ReduxConnector.connect(neededStoreVariables, Handler.updateProperties);

export default Handler;
